import { keepPreviousData } from "@tanstack/react-query";
import { useDotLegalPostGetQueryArray } from "@common/hooks/dotLegalReactQuery";

// The endpoint returns only the entities the user can open, so an id missing from the result is one that must get
// neither a link nor its details. Ids are sorted so reordering the cards does not refetch, and the previous result is
// kept while a changed list loads so the existing cards' links do not blink.
export function useAccessibleLinkedEntities<T extends { id: string }>(url: string, ids: Array<string>, isEnabled: boolean) {
    const sortedIds = [...ids].sort();
    const accessibleEntitiesQuery = useDotLegalPostGetQueryArray<Array<T>>([url, ...sortedIds], url, sortedIds, {
        enabled: isEnabled && ids.length > 0,
        placeholderData: keepPreviousData,
    });

    function getAccessibleEntity(id: string) {
        return accessibleEntitiesQuery.data?.find((entity) => entity.id === id);
    }

    // A newly picked id is missing from the kept result until the changed list has loaded. A refetch of the same list
    // keeps its result, so an entity the user cannot open does not flicker into loading on every window focus.
    function isEntityLoading(id: string) {
        return (accessibleEntitiesQuery.isLoading || accessibleEntitiesQuery.isPlaceholderData) && !getAccessibleEntity(id);
    }

    return { getAccessibleEntity, isEntityLoading };
}
